const neutral = {
  90: '#202328',
  80: '#32373E',
  70: '#424952',
  60: '#5F6771',
  50: '#6F7780',
  40: '#8E959E',
  30: '#A1A8B0',
  20: '#CED3DA',
  10: '#E7EBEE',
  5: '#F4F5F7',
} as const;

const blue = {
  90: '#1E3251',
  80: '#1B417A',
  70: '#1950A3',
  60: '#165FCC',
  50: '#146EF5',
  40: '#3F8BFD',
  30: '#6BA6FF',
  20: '#9EC5FF',
  10: '#DCEBFE',
  5: '#EBF3FF',
} as const;

const green = {
  90: '#1B372D',
  80: '#164B31',
  70: '#125F36',
  60: '#0D733A',
  50: '#08873F',
  40: '#0BA84F',
  30: '#13C35F',
  20: '#95E4AA',
  10: '#C5F6DA',
  5: '#E5FBEE',
} as const;

const red = {
  90: '#46262A',
  80: '#6C292C',
  70: '#912B2D',
  60: '#B72E2F',
  50: '#DD3131',
  40: '#FF584C',
  30: '#FF837A',
  20: '#FFB8B3',
  10: '#FFE3E0',
  5: '#FFF1F0',
} as const;

const yellow = {
  90: '#393120',
  80: '#524019',
  70: '#6B4E11',
  60: '#845D0A',
  50: '#9D6B02',
  40: '#B88400',
  30: '#D39F03',
  20: '#F2BE04',
  10: '#FFE895',
  5: '#FFF6D6',
} as const;

const seaweed = {
  90: '#1A3638',
  80: '#144A48',
  70: '#0D5D59',
  60: '#077169',
  50: '#018479',
  40: '#07A198',
  30: '#22CBC1',
  20: '#89E1DB',
  10: '#C2F4F1',
  5: '#E0FAF8',
} as const;

const eggplant = {
  90: '#442748',
  80: '#5F2F6A',
  70: '#7B368D',
  60: '#963EAF',
  50: '#B245D1',
  40: '#C467DE',
  30: '#D88EEC',
  20: '#E5AFF3',
  10: '#F8E3FD',
  5: '#FCF1FE',
} as const;

const rose = {
  90: '#4D1D37',
  80: '#721A48',
  70: '#961659',
  60: '#BB136A',
  50: '#E0107B',
  40: '#F358A8',
  30: '#F9A8D4',
  20: '#FBCFE8',
  10: '#FCE7F3',
  5: '#FCEEF6',
} as const;

const indigo = {
  90: '#342E4F',
  80: '#3F3B78',
  70: '#4B49A2',
  60: '#5656CB',
  50: '#6264F4',
  40: '#8788F8',
  30: '#A5B4FC',
  20: '#C7D2FE',
  10: '#E0E7FF',
  5: '#EEF2FF',
} as const;

const tangerine = {
  90: '#472B1E',
  80: '#663617',
  70: '#84410F',
  60: '#A34C08',
  50: '#C25700',
  40: '#F06F05',
  30: '#FDBA74',
  20: '#FED7AA',
  10: '#FFEDD5',
  5: '#FFF7ED',
} as const;

const getColorWithAlpha = <T extends string>({ base, opacity }: { base: T; opacity: number }): T | `${T}${string}` => {
  const val = Math.round((opacity / 100) * 255);
  const alpha = val.toString(16);

  return `${base}${alpha}`;
};

const getColorGenerator =
  <T extends string>(base: T) =>
  (opacity?: number) =>
    opacity !== undefined ? getColorWithAlpha({ base, opacity }) : base;

export const colorsV2 = {
  neutral90: getColorGenerator(neutral[90]),
  neutral80: getColorGenerator(neutral[80]),
  neutral70: getColorGenerator(neutral[70]),
  neutral60: getColorGenerator(neutral[60]),
  neutral50: getColorGenerator(neutral[50]),
  neutral40: getColorGenerator(neutral[40]),
  neutral30: getColorGenerator(neutral[30]),
  neutral20: getColorGenerator(neutral[20]),
  neutral10: getColorGenerator(neutral[10]),
  neutral5: getColorGenerator(neutral[5]),

  blue90: getColorGenerator(blue[90]),
  blue80: getColorGenerator(blue[80]),
  blue70: getColorGenerator(blue[70]),
  blue60: getColorGenerator(blue[60]),
  blue50: getColorGenerator(blue[50]),
  blue40: getColorGenerator(blue[40]),
  blue30: getColorGenerator(blue[30]),
  blue20: getColorGenerator(blue[20]),
  blue10: getColorGenerator(blue[10]),
  blue5: getColorGenerator(blue[5]),

  green90: getColorGenerator(green[90]),
  green80: getColorGenerator(green[80]),
  green70: getColorGenerator(green[70]),
  green60: getColorGenerator(green[60]),
  green50: getColorGenerator(green[50]),
  green40: getColorGenerator(green[40]),
  green30: getColorGenerator(green[30]),
  green20: getColorGenerator(green[20]),
  green10: getColorGenerator(green[10]),
  green5: getColorGenerator(green[5]),

  red90: getColorGenerator(red[90]),
  red80: getColorGenerator(red[80]),
  red70: getColorGenerator(red[70]),
  red60: getColorGenerator(red[60]),
  red50: getColorGenerator(red[50]),
  red40: getColorGenerator(red[40]),
  red30: getColorGenerator(red[30]),
  red20: getColorGenerator(red[20]),
  red10: getColorGenerator(red[10]),
  red5: getColorGenerator(red[5]),

  yellow90: getColorGenerator(yellow[90]),
  yellow80: getColorGenerator(yellow[80]),
  yellow70: getColorGenerator(yellow[70]),
  yellow60: getColorGenerator(yellow[60]),
  yellow50: getColorGenerator(yellow[50]),
  yellow40: getColorGenerator(yellow[40]),
  yellow30: getColorGenerator(yellow[30]),
  yellow20: getColorGenerator(yellow[20]),
  yellow10: getColorGenerator(yellow[10]),
  yellow5: getColorGenerator(yellow[5]),

  seaweed90: getColorGenerator(seaweed[90]),
  seaweed80: getColorGenerator(seaweed[80]),
  seaweed70: getColorGenerator(seaweed[70]),
  seaweed60: getColorGenerator(seaweed[60]),
  seaweed50: getColorGenerator(seaweed[50]),
  seaweed40: getColorGenerator(seaweed[40]),
  seaweed30: getColorGenerator(seaweed[30]),
  seaweed20: getColorGenerator(seaweed[20]),
  seaweed10: getColorGenerator(seaweed[10]),
  seaweed5: getColorGenerator(seaweed[5]),

  rose90: getColorGenerator(rose[90]),
  rose80: getColorGenerator(rose[80]),
  rose70: getColorGenerator(rose[70]),
  rose60: getColorGenerator(rose[60]),
  rose50: getColorGenerator(rose[50]),
  rose40: getColorGenerator(rose[40]),
  rose30: getColorGenerator(rose[30]),
  rose20: getColorGenerator(rose[20]),
  rose10: getColorGenerator(rose[10]),
  rose5: getColorGenerator(rose[5]),

  eggplant90: getColorGenerator(eggplant[90]),
  eggplant80: getColorGenerator(eggplant[80]),
  eggplant70: getColorGenerator(eggplant[70]),
  eggplant60: getColorGenerator(eggplant[60]),
  eggplant50: getColorGenerator(eggplant[50]),
  eggplant40: getColorGenerator(eggplant[40]),
  eggplant30: getColorGenerator(eggplant[30]),
  eggplant20: getColorGenerator(eggplant[20]),
  eggplant10: getColorGenerator(eggplant[10]),
  eggplant5: getColorGenerator(eggplant[5]),

  indigo90: getColorGenerator(indigo[90]),
  indigo80: getColorGenerator(indigo[80]),
  indigo70: getColorGenerator(indigo[70]),
  indigo60: getColorGenerator(indigo[60]),
  indigo50: getColorGenerator(indigo[50]),
  indigo40: getColorGenerator(indigo[40]),
  indigo30: getColorGenerator(indigo[30]),
  indigo20: getColorGenerator(indigo[20]),
  indigo10: getColorGenerator(indigo[10]),
  indigo5: getColorGenerator(indigo[5]),

  tangerine90: getColorGenerator(tangerine[90]),
  tangerine80: getColorGenerator(tangerine[80]),
  tangerine70: getColorGenerator(tangerine[70]),
  tangerine60: getColorGenerator(tangerine[60]),
  tangerine50: getColorGenerator(tangerine[50]),
  tangerine40: getColorGenerator(tangerine[40]),
  tangerine30: getColorGenerator(tangerine[30]),
  tangerine20: getColorGenerator(tangerine[20]),
  tangerine10: getColorGenerator(tangerine[10]),
  tangerine5: getColorGenerator(tangerine[5]),
};
